import { FC, useState } from 'react'
import {
  Box,
  Button,
  Card,
  // CardActions,
  CardContent,
  CardMedia,
  Grid,
  H3,
  H5,
  IconButton,
  Link,
  makeStyles,
  PageTitle,
} from '@perk-ui/core'
import { ArrowLeft, FileText } from 'react-feather'

import DataPair from '../../components/DataPair'
import { useSitesContext } from '../../components/SitesProvider'
import TableauView from '../../components/TableauView'
import config from '../../config/app-config'
// import { useUser } from '../../features/auth/AuthContext'
import { Study } from '../../features/query-hooks/useStudies'
// import { keyToLabel } from '../Home/components/BodyPartGraph'

export interface MyStudiesProps {}

const useStyles = makeStyles((theme) => ({
  emptyContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    width: '100%',
  },
  emptyStateIcon: {
    display: 'flex',
    backgroundColor: '#F1F1F1',
    borderRadius: '50%',
    marginBottom: theme.spacing(2),
    '& svg': {
      color: '#6B6B6B',
    },
  },
  emptyStateTitle: {
    color: '#6B6B6B',
    fontWeight: 700,
    fontSize: '16px',
    marginBottom: theme.spacing(1),
  },
  emptyStateText: {
    color: '#6B6B6B',
    fontWeight: 400,
    fontSize: '12px',
    marginBottom: theme.spacing(2),
  },
  emptyStateButton: {
    fontSize: '14px',
    fontWeight: 400,
    marginBottom: theme.spacing(2),
    minHeight: 40,
    padding: theme.spacing(1, 3),
  },
  studyCard: {
    display: 'flex',
    padding: theme.spacing(2),
    cursor: 'pointer',
    border: 'solid 1px transparent',
    height: '100%',
    '&:hover': {
      opacity: '0.8',
      border: 'solid 1px #b3b3b3',
    },
  },
  studyCardMedia: {
    // backgroundImage: 'url(' + process.env.PUBLIC_URL + '/assets/card-gradient.jpg)',
    backgroundSize: 'contain',
    backgroundRepeat: 'no-repeat',
    borderRadius: '12px',
    padding: theme.spacing(0, 2),
    // marginBottom: 'auto',
    objectFit: 'contain',
    objectPosition: 'top',
    width: '30%',
    height: '150px',
  },
  studyCardContent: {
    width: '70%',
    padding: theme.spacing(0, 3),
    '& h3': {
      marginBottom: theme.spacing(1),
    },
    '& span': {
      fontWeight: 700,
    },
  },
  studyCardActions: {
    padding: 0,
    '& button': {
      padding: theme.spacing(1, 2),
      borderRadius: '16px',
    },
  },
}))

const MyStudies: FC<MyStudiesProps> = () => {
  const sitesContext = useSitesContext()
  const [activeStudy, setActiveStudy] = useState<Study | null>(null)
  // const currentUser = useUser()
  const classes = useStyles()
  const site = sitesContext?.currentSite
  const siteStudies = site?.studies || []
  // const staffTableauSubscription = currentUser?.staff.subscriptionPlan

  const pageTitle = 'My Studies'
  // const workbookUrl = ''

  if (!site) return null

  const emptyStatePage = () => {
    const icon = <FileText />
    const title = 'No Studies'
    const pageText = 'You are currently not part of any Studies.'
    const buttonText = 'Learn more about Studies'
    const buttonLink = 'https://www.databiologics.com/active-research'

    return (
      <Box p={3} height={'100%'}>
        <Box className={classes.emptyContainer}>
          <Box className={classes.emptyStateIcon} p={2}>
            {icon}
          </Box>
          <H3 className={classes.emptyStateTitle}>{title}</H3>
          <H5 className={classes.emptyStateText}>{pageText}</H5>
          <Link
            component={Button}
            className={classes.emptyStateButton}
            href={buttonLink}
            target="_blank"
          >
            {buttonText}
          </Link>
        </Box>
      </Box>
    )
  }

  const getPrincipalInvestigatorNames = (study: Study) => {
    return (study?.siteStudies || [])
      .filter((study) => study.role === 'Principal Investigator')
      .map((investigator, index) => {
        if (index > 1) return ''
        return `${index === 1 ? ' & ' : ''}${investigator?.site?.shortName}`
      })
      .join('')
  }

  const getWorkbookUrl = () => {
    switch (activeStudy?.level) {
      case 'standard':
        return 'views/impact_study_standard_2025/StandardStudyHome'
      case 'premium':
        return 'views/impact_study_premium_2025/PremiumStudyHome'
      default:
        return ''
    }
  }

  const myStudiesTableauParameters = [
    {
      key: 'parameter_select_subscription_type',
      value: 'STUDY',
    },
    // {
    //   key: 'parameter_select_study_id',
    //   value: activeStudy?.id,
    // },
    // {
    //   key: 'parameter_select_provider_id',
    //   value: currentUser?.staff?.id,
    // },
  ]

  const filteredSiteStudies = siteStudies.filter((study) =>
    site?.siteStudies.find(
      (siteStud) =>
        siteStud.studyId == study.id &&
        siteStud.status == 'active' &&
        study.status == 'active',
    ),
  )
  return (
    <Box>
      <PageTitle title={pageTitle} />
      {filteredSiteStudies.length == 0 && emptyStatePage()}
      {filteredSiteStudies.length > 0 && !activeStudy && (
        <Grid container spacing={2} p={3}>
          {filteredSiteStudies.map((study) => (
            <Grid item xs={12} sm={6} key={study.id}>
              <Card
                key={study.id}
                className={classes.studyCard}
                onClick={() => setActiveStudy(study)}
              >
                {/* <Box display="flex" flexDirection="column" justifyContent="space-between" height="100%">
                  <Box> */}
                <CardMedia
                  component="img"
                  className={classes.studyCardMedia}
                  sx={{ width: 151 }}
                  image={
                    study.imageUrl
                      ? config.cdnPath + '/' + study.imageUrl
                      : process.env.PUBLIC_URL + '/assets/default-dbio.png'
                  }
                  alt={study.name}
                />
                <CardContent className={classes.studyCardContent}>
                  <H3>{study.name} Study</H3>
                  {/* <DataPair
                        label={
                          study.specialty.length > 1 ? 'Specialties' : 'Specialty'
                        }
                        value={study.specialty.join(', ')}
                      /> */}
                  <DataPair
                    label="Principal Investigators"
                    value={getPrincipalInvestigatorNames(study)}
                  />
                  <DataPair label="Sponsor" value={study.sponsor?.name} />
                  <DataPair label="Description" value={study.description} />
                  {/* <DataPair label="Level" value={keyToLabel(study.level)} /> */}
                </CardContent>
                {/* </Box>
                  <Box>
                    <CardActions className={classes.studyCardActions}>
                      <Button
                        endIcon={<ArrowRight />}
                        fullWidth
                        size="small"
                        onClick={() => setActiveStudy(study)}
                      >
                        View
                      </Button>
                    </CardActions>
                  </Box>
                </Box> */}
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {activeStudy && (
        <Box height={'100%'}>
          <Box display="flex" alignItems={'center'} p={2}>
            <IconButton onClick={() => setActiveStudy(null)}>
              <ArrowLeft />
            </IconButton>
            <H3>{activeStudy.name} Study</H3>
          </Box>
          <TableauView
            src={getWorkbookUrl()}
            toolbar="top"
            hideTabs={false}
            parameters={myStudiesTableauParameters}
            section="my-studies"
            id={activeStudy.id}
          />
        </Box>
      )}
    </Box>
  )
}

export default MyStudies
